import type {
  PublicKeyCredentialCreationOptionsJSON,
  PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import {
  browserSupportsWebAuthn,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import type {
  CandidatesListResponse,
  ContactDetailDto,
  ContactsListResponse,
  ContactTimelineResponse,
  DecisionKind,
  FeedbackType,
  FollowupsListResponse,
  LearningMetricsDto,
  ReviewStatus,
  ThreadDetailDto,
  ThreadsListResponse,
  ThreadsSummary,
} from '../types.js';
import {
  AUTH_MODE,
  type AuthMode,
  BEARER,
  isBearerAuthEnabled,
  isCookieAuthEnabled,
} from './auth-config.js';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? 'http://127.0.0.1:3000').replace(/\/+$/, '');

/** batch-176 — the real-time SSE stream URL (same origin/base as the REST API). */
export function realtimeUrl(): string {
  return `${BASE_URL}/events`;
}

export type ApiErrorKind =
  | 'unauthorized' // 401
  | 'forbidden' // 403
  | 'not_found' // 404
  | 'conflict' // 409 — e.g. analyze force-mismatch
  | 'rate_limited' // 429
  | 'server' // 5xx
  | 'network' // fetch failed entirely
  | 'unknown';

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  readonly url: string;
  readonly body: string;

  constructor(kind: ApiErrorKind, status: number, url: string, body: string) {
    super(`api ${status} ${kind}`);
    this.kind = kind;
    this.status = status;
    this.url = url;
    this.body = body;
  }
}

/**
 * batch-177 — operator-readable error text. The server returns `{error, code}` on
 * a handled failure; surface that human message instead of the opaque
 * `api <status> <kind>`. Falls back to the generic message / `fallback`.
 */
export function serverErrorMessage(e: unknown, fallback = 'request failed'): string {
  if (e instanceof ApiError) {
    try {
      const parsed = JSON.parse(e.body) as { error?: unknown };
      if (typeof parsed?.error === 'string' && parsed.error.trim()) return parsed.error.trim();
    } catch {
      /* body not JSON — fall through to the generic message */
    }
    return e.message;
  }
  return e instanceof Error ? e.message : fallback;
}

interface FetchOptions {
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
  /** Override the configured auth mode (tests only). */
  authMode?: AuthMode;
  /**
   * Force `credentials: 'include'` regardless of auth mode. The WebAuthn
   * ceremony hands the server-set challenge cookie back on the verify call, so
   * those calls must send cookies even in bearer mode (where the bearer header
   * still authenticates the request — the two are not exclusive).
   */
  forceCredentialsInclude?: boolean;
}

/**
 * Build the request init for a JSON call against the read API. Centralized
 * so every method picks up the same auth-mode rules.
 *
 * Never logs the bearer or any cookie. Never reads passwords. Never reads
 * window.localStorage / sessionStorage — passwords are passed in-memory
 * to login() only.
 */
function buildRequestInit(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  body: unknown,
  opts: FetchOptions,
): RequestInit {
  const mode: AuthMode = opts.authMode ?? AUTH_MODE;
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (isBearerAuthEnabled(mode)) headers.Authorization = `Bearer ${BEARER}`;

  return {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: opts.signal,
    credentials:
      opts.forceCredentialsInclude || isCookieAuthEnabled(mode) ? 'include' : 'same-origin',
  };
}

async function request<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body: unknown,
  opts: FetchOptions,
): Promise<T> {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch;
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const init = buildRequestInit(method, body, opts);

  let res: Response;
  try {
    res = await fetchImpl(url, init);
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError('network', 0, url, err instanceof Error ? err.message : String(err));
  }

  if (!res.ok) {
    const text = await safeReadText(res);
    throw new ApiError(kindFromStatus(res.status), res.status, url, text);
  }

  // 204 No Content or empty body — return undefined safely.
  const text = await safeReadText(res);
  return (text.length === 0 ? undefined : JSON.parse(text)) as T;
}

export async function apiGet<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  return request<T>('GET', path, undefined, opts);
}

/**
 * Resolve an API path to a browser-usable URL. A leading-slash path (e.g. a
 * server-returned `/contacts/avatar/:id`) is prefixed with the configured API
 * base so an <img src> hits the API same-origin; absolute URLs pass through.
 * Used for cached avatars (served by the API) vs external image URLs.
 */
export function apiUrl(path: string): string {
  return path.startsWith('/') ? `${BASE_URL}${path}` : path;
}

/**
 * batch-203 — same-origin URL for a message attachment served by the media proxy.
 * An <img>/<video>/<a download> hitting this sends the session cookie
 * automatically (same-origin), so attachments render without hotlinking provider
 * media. ids are path-encoded.
 */
export function attachmentUrl(messageId: string, attachmentId: string): string {
  return apiUrl(
    `/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(attachmentId)}`,
  );
}

async function apiPost<T>(path: string, body: unknown, opts: FetchOptions = {}): Promise<T> {
  return request<T>('POST', path, body, opts);
}

async function apiPatch<T>(path: string, body: unknown, opts: FetchOptions = {}): Promise<T> {
  return request<T>('PATCH', path, body, opts);
}

async function apiPut<T>(path: string, body: unknown, opts: FetchOptions = {}): Promise<T> {
  return request<T>('PUT', path, body, opts);
}

async function apiDelete<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  return request<T>('DELETE', path, undefined, opts);
}

async function safeReadText(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return '';
  }
}

function kindFromStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthorized';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server';
  return 'unknown';
}

export interface ListThreadsParams {
  limit?: number;
  before?: string;
  channel?: string;
  /** Filter by exact review status. */
  status?: ReviewStatus | ReviewStatus[];
  /** Latest-message direction filter. */
  direction?: 'inbound' | 'outbound';
  /**
   * Shorthand: latest message is inbound AND status is in ('new',
   * 'needs_review'). The server rejects this combined with explicit
   * `direction` or `status`.
   */
  actionable?: boolean;
  /** Free-text substring match across display_name / handle / external_id. */
  q?: string;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

export async function listThreads(params: ListThreadsParams = {}): Promise<ThreadsListResponse> {
  const search = new URLSearchParams();
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.before !== undefined) search.set('before', params.before);
  if (params.channel !== undefined) search.set('channel', params.channel);
  if (params.direction !== undefined) search.set('direction', params.direction);
  if (params.actionable !== undefined) search.set('actionable', String(params.actionable));
  if (params.q !== undefined) search.set('q', params.q);
  if (params.status !== undefined) {
    const list = Array.isArray(params.status) ? params.status : [params.status];
    for (const s of list) search.append('status', s);
  }
  const qs = search.toString();
  return apiGet<ThreadsListResponse>(`/threads${qs ? `?${qs}` : ''}`, {
    signal: params.signal,
    fetchImpl: params.fetchImpl,
  });
}

export interface GetSummaryParams {
  channel?: string;
  /** R14 — search term, so the Needs-reply / All counts match the filtered list. */
  q?: string;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

export async function getThreadsSummary(params: GetSummaryParams = {}): Promise<ThreadsSummary> {
  const search = new URLSearchParams();
  if (params.channel !== undefined) search.set('channel', params.channel);
  if (params.q !== undefined && params.q !== '') search.set('q', params.q);
  const qs = search.toString();
  return apiGet<ThreadsSummary>(`/threads/summary${qs ? `?${qs}` : ''}`, {
    signal: params.signal,
    fetchImpl: params.fetchImpl,
  });
}

export interface GetLearningMetricsParams {
  since?: string;
  until?: string;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

/** batch-6 — calibration metrics for the Teach panel. Read-only, aggregate. */
export async function getLearningMetrics(
  params: GetLearningMetricsParams = {},
): Promise<LearningMetricsDto> {
  const search = new URLSearchParams();
  if (params.since) search.set('since', params.since);
  if (params.until) search.set('until', params.until);
  const qs = search.toString();
  return apiGet<LearningMetricsDto>(`/threads/learning-metrics${qs ? `?${qs}` : ''}`, {
    signal: params.signal,
    fetchImpl: params.fetchImpl,
  });
}

/* ---------- batch 17 / 18: follow-ups ---------- */

export type FollowupIncludeReason =
  | 'deferred'
  | 'stale_actionable'
  | 'i_owe_reply'
  | 'waiting_on_them';

export interface ListFollowupsParams {
  /** Cut-off for stale_actionable. Defaults server-side to 7d. */
  stale_days?: number;
  /** Optional channel filter (mirrors the threads list). */
  channel?: string;
  /**
   * Which derived buckets to include. Server default is
   * `['deferred', 'stale_actionable']`; pass all four for the full
   * "things on my plate" view added in batch 18.
   */
  include?: FollowupIncludeReason[];
  limit?: number;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

export async function listFollowups(
  params: ListFollowupsParams = {},
): Promise<FollowupsListResponse> {
  const search = new URLSearchParams();
  if (params.stale_days !== undefined) search.set('stale_days', String(params.stale_days));
  if (params.channel !== undefined) search.set('channel', params.channel);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.include !== undefined) {
    for (const r of params.include) search.append('include', r);
  }
  const qs = search.toString();
  return apiGet<FollowupsListResponse>(`/threads/followups${qs ? `?${qs}` : ''}`, {
    signal: params.signal,
    fetchImpl: params.fetchImpl,
  });
}

export interface GetThreadParams {
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

export async function getThread(
  id: string,
  params: GetThreadParams = {},
): Promise<ThreadDetailDto> {
  return apiGet<ThreadDetailDto>(`/threads/${encodeURIComponent(id)}`, params);
}

export interface ApiPostOptions {
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

export interface SaveDraftResponse {
  draft_id: string;
  version: number;
  decision_id: string;
  review_status: ReviewStatus;
  /**
   * B29 — set to 'accepted' / 'edited' when the request included
   * `from_analysis_id` and the server emitted an auto learning signal;
   * null otherwise (manual draft).
   */
  auto_feedback_type: 'accepted' | 'edited' | null;
}

export function saveDraft(
  threadId: string,
  body: {
    body_text: string;
    subject?: string | null;
    /** B29 — pointer to the analysis whose proposed_response seeded this draft. */
    from_analysis_id?: string | null;
  },
  opts: ApiPostOptions = {},
): Promise<SaveDraftResponse> {
  return apiPost<SaveDraftResponse>(`/threads/${encodeURIComponent(threadId)}/drafts`, body, opts);
}

export interface CreateCommentResponse {
  comment_id: string;
  review_status: ReviewStatus;
}

export function createComment(
  threadId: string,
  body: { body: string },
  opts: ApiPostOptions = {},
): Promise<CreateCommentResponse> {
  return apiPost<CreateCommentResponse>(
    `/threads/${encodeURIComponent(threadId)}/comments`,
    body,
    opts,
  );
}

export interface CreateDecisionResponse {
  decision_id: string;
  review_status: ReviewStatus;
  /**
   * B29 — set to 'no_response' when the decision was
   * `mark_no_response_needed` and the server emitted an auto learning
   * signal. null for all other decision kinds.
   */
  auto_feedback_type: 'no_response' | null;
}

export function createDecision(
  threadId: string,
  body: { kind: DecisionKind; draft_id?: string; notes?: string },
  opts: ApiPostOptions = {},
): Promise<CreateDecisionResponse> {
  return apiPost<CreateDecisionResponse>(
    `/threads/${encodeURIComponent(threadId)}/decisions`,
    body,
    opts,
  );
}

/** B3.11 — kinds that can be applied in bulk (no save_draft / needs_review). */
export type BulkDecisionKind = 'defer' | 'mark_no_response_needed';

export interface BulkDecisionResult {
  thread_id: string;
  status: 'ok' | 'not_found';
  decision_id?: string;
  review_status?: ReviewStatus;
}

export interface BulkDecisionsResponse {
  applied: number;
  not_found: number;
  results: BulkDecisionResult[];
}

/**
 * B3.11 — apply one decision kind across many threads in a single request.
 * Replaces the old client-side fan-out of N createDecision calls; the
 * server records one operator_decision per thread in one transaction.
 */
export function createBulkDecisions(
  body: { thread_ids: string[]; kind: BulkDecisionKind; notes?: string },
  opts: ApiPostOptions = {},
): Promise<BulkDecisionsResponse> {
  return apiPost<BulkDecisionsResponse>('/threads/decisions/bulk', body, opts);
}

/**
 * batch-144 — inbox-zero: retroactively mark-read every actionable thread the AI
 * already judged needs no response.
 *
 * R14b (#9) — CHUNKED: clears up to `limit` per call and returns `remaining`, so the
 * UI can loop until `remaining === 0` while showing a determinate "N of M" bar.
 */
export function clearNoReply(
  limit?: number,
  opts: ApiPostOptions = {},
): Promise<{ cleared: number; remaining: number }> {
  return apiPost<{ cleared: number; remaining: number }>(
    '/threads/clear-no-reply',
    limit != null ? { limit } : {},
    opts,
  );
}

/**
 * R5b — restore a thread to the actionable `needs_review` state. The UNDO for
 * "Done" (mark_no_response_needed); status-only + reversible. Consumed by R15's
 * undo toast.
 */
export function reopenThread(
  threadId: string,
  opts: ApiPostOptions = {},
): Promise<{ review_status: string }> {
  return apiPost<{ review_status: string }>(
    `/threads/${encodeURIComponent(threadId)}/reopen`,
    {},
    opts,
  );
}

export interface TriggerAnalysisResponse {
  job_id: string;
  thread_id: string;
  force: boolean;
  /**
   * `true` when the API recognised an in-flight analyze job for this
   * thread and returned its id instead of enqueueing another. The UI
   * treats coalesced and freshly-queued the same way for polling
   * purposes — both mean "wait for a result".
   */
  coalesced: boolean;
}

export function triggerAnalysis(
  threadId: string,
  body: { force?: boolean; steering_note?: string } = {},
  opts: ApiPostOptions = {},
): Promise<TriggerAnalysisResponse> {
  return apiPost<TriggerAnalysisResponse>(
    `/threads/${encodeURIComponent(threadId)}/analyze`,
    body,
    opts,
  );
}

/**
 * Lightweight poll target for `IntelligencePanel` — returns the live
 * pipeline state without refetching the whole thread (which would
 * trigger the auto-scroll in ThreadDetail; see B1-H1).
 *
 * Pass `sinceAnalysisId` (the analysis id the client last knew about)
 * so the server can compute `result_relation` for us — that way the
 * response body never contains a raw analysis id.
 */
export function getAnalysisStatus(
  threadId: string,
  opts: {
    signal?: AbortSignal;
    fetchImpl?: typeof fetch;
    sinceAnalysisId?: string | null;
  } = {},
): Promise<import('../types.js').AnalysisStatusDto> {
  const qs =
    opts.sinceAnalysisId !== null && opts.sinceAnalysisId !== undefined
      ? `?since_analysis_id=${encodeURIComponent(opts.sinceAnalysisId)}`
      : '';
  return apiGet<import('../types.js').AnalysisStatusDto>(
    `/threads/${encodeURIComponent(threadId)}/analysis-status${qs}`,
    opts,
  );
}

/* ---------- contacts / address book (batch 12) ---------- */

export interface ListContactsParams {
  limit?: number;
  before?: string;
  channel?: string;
  q?: string;
  /** batch-189 — address book: which archive state to include (default active). */
  archived?: 'active' | 'archived' | 'all';
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

export async function listContacts(params: ListContactsParams = {}): Promise<ContactsListResponse> {
  const search = new URLSearchParams();
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  if (params.before !== undefined) search.set('before', params.before);
  if (params.channel !== undefined) search.set('channel', params.channel);
  if (params.q !== undefined) search.set('q', params.q);
  if (params.archived !== undefined) search.set('archived', params.archived);
  const qs = search.toString();
  return apiGet<ContactsListResponse>(`/contacts${qs ? `?${qs}` : ''}`, {
    signal: params.signal,
    fetchImpl: params.fetchImpl,
  });
}

export interface GetContactParams {
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

export async function getContact(
  id: string,
  params: GetContactParams = {},
): Promise<ContactDetailDto> {
  return apiGet<ContactDetailDto>(`/contacts/${encodeURIComponent(id)}`, params);
}

/** GET /contacts/:id/timeline — unified cross-channel message timeline. */
export async function getContactTimeline(
  id: string,
  params: { limit?: number; before?: string } & GetContactParams = {},
): Promise<ContactTimelineResponse> {
  const search = new URLSearchParams();
  if (params.limit) search.set('limit', String(params.limit));
  if (params.before) search.set('before', params.before);
  const qs = search.toString();
  return apiGet<ContactTimelineResponse>(
    `/contacts/${encodeURIComponent(id)}/timeline${qs ? `?${qs}` : ''}`,
    params,
  );
}

/** POST /contacts/merge — merge `source` INTO `target`. Reversible. */
export function mergeContacts(
  sourceContactId: string,
  targetContactId: string,
  opts: ApiPostOptions = {},
): Promise<{ merge_id: string; target_contact_id: string; moved_identity_count: number }> {
  return apiPost<{ merge_id: string; target_contact_id: string; moved_identity_count: number }>(
    '/contacts/merge',
    { source_contact_id: sourceContactId, target_contact_id: targetContactId },
    opts,
  );
}

/** POST /contacts/identities/:id/reassign — move one channel identity to a contact. */
export function reassignIdentity(
  contactIdentityId: string,
  targetContactId: string,
  opts: ApiPostOptions = {},
): Promise<{ merge_id: string; target_contact_id: string }> {
  return apiPost<{ merge_id: string; target_contact_id: string }>(
    `/contacts/identities/${encodeURIComponent(contactIdentityId)}/reassign`,
    { target_contact_id: targetContactId },
    opts,
  );
}

/**
 * batch-101 — POST /threads/:id/assign-contact: assign / create a contact FROM
 * the open conversation, even when the thread has no linked counterparty yet
 * ("Unknown contact"). Server-side it materializes the counterparty identity
 * from the chat roster on demand, then links it. Reversible.
 *   - { mode: 'existing', contact_id }
 *   - { mode: 'new', display_name, company?, role?, location? }
 */
export type AssignThreadContactBody =
  | { mode: 'existing'; contact_id: string }
  | {
      mode: 'new';
      display_name: string;
      company?: string | null;
      role?: string | null;
      location?: string | null;
    };

export function assignThreadContact(
  threadId: string,
  body: AssignThreadContactBody,
  opts: ApiPostOptions = {},
): Promise<{ contact_id: string; identity_id: string; merge_id?: string; enriched?: boolean }> {
  return apiPost(`/threads/${encodeURIComponent(threadId)}/assign-contact`, body, opts);
}

/**
 * batch-106 — mark a CONVERSATION non-actionable ("muted") or un-mute it. Muted
 * threads drop out of the "Needs reply" queue but stay under "All".
 */
export function setThreadMuted(
  threadId: string,
  muted: boolean,
  opts: ApiPostOptions = {},
): Promise<{ id: string; muted: boolean }> {
  return apiPost(`/threads/${encodeURIComponent(threadId)}/mute`, { muted }, opts);
}

/**
 * batch-149 — snooze a CONVERSATION out of "Needs reply" until `until` (ISO),
 * or pass `until: null` to un-snooze now. A snooze self-expires (the thread
 * reappears once the time passes); a new inbound message also lifts it.
 */
export function snoozeThread(
  threadId: string,
  until: string | null,
  opts: ApiPostOptions = {},
): Promise<{ id: string; snooze_until: string | null }> {
  return apiPost(`/threads/${encodeURIComponent(threadId)}/snooze`, { until }, opts);
}

/**
 * batch-106 — mark a CONTACT non-actionable ("muted") or un-mute. Muted ⇒ all of
 * the contact's conversations leave the "Needs reply" queue (still under "All").
 */
export function setContactMuted(
  contactId: string,
  muted: boolean,
  opts: ApiPostOptions = {},
): Promise<{ id: string; muted: boolean }> {
  return apiPost(`/contacts/${encodeURIComponent(contactId)}/mute`, { muted }, opts);
}

/** batch-189 — pin/unpin a contact to the ★ Favorites section. Reversible. */
export function setContactFavorite(
  contactId: string,
  favorite: boolean,
  opts: ApiPostOptions = {},
): Promise<{ id: string; is_favorite: boolean }> {
  return apiPost(`/contacts/${encodeURIComponent(contactId)}/favorite`, { favorite }, opts);
}

/**
 * batch-189 — "Delete contact" = reversible archive (soft-delete). archived=true
 * removes it from the address book; archived=false restores it. Threads/history
 * are kept; nothing is permanently deleted.
 */
export function setContactArchived(
  contactId: string,
  archived: boolean,
  opts: ApiPostOptions = {},
): Promise<{ id: string; archived_at: string | null }> {
  return apiPost(`/contacts/${encodeURIComponent(contactId)}/archive`, { archived }, opts);
}

/** batch-189 — create a contact manually from the address book ("+ New"). */
export function createContact(
  body: {
    display_name: string;
    company?: string | null;
    role?: string | null;
    location?: string | null;
  },
  opts: ApiPostOptions = {},
): Promise<{
  contact_id: string;
  display_name: string;
  company: string | null;
  role: string | null;
  location: string | null;
}> {
  return apiPost('/contacts', body, opts);
}

/** POST /contacts/merges/:id/undo — reverse a merge/reassign. */
export function undoMerge(
  mergeId: string,
  opts: ApiPostOptions = {},
): Promise<{ ok: true; undone_merge_id: string }> {
  return apiPost<{ ok: true; undone_merge_id: string }>(
    `/contacts/merges/${encodeURIComponent(mergeId)}/undo`,
    undefined,
    opts,
  );
}

/* ---------- batch 16: candidates + language preference ---------- */

export interface ListCandidatesParams {
  status?: 'pending' | 'confirmed' | 'rejected';
  limit?: number;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

export async function listCandidates(
  params: ListCandidatesParams = {},
): Promise<CandidatesListResponse> {
  const search = new URLSearchParams();
  if (params.status) search.set('status', params.status);
  if (params.limit !== undefined) search.set('limit', String(params.limit));
  const qs = search.toString();
  return apiGet<CandidatesListResponse>(`/contacts/candidates${qs ? `?${qs}` : ''}`, {
    signal: params.signal,
    fetchImpl: params.fetchImpl,
  });
}

export interface DecideCandidateResponse {
  id: string;
  status: 'pending' | 'confirmed' | 'rejected';
}

export async function confirmCandidate(
  id: string,
  opts: ApiPostOptions = {},
): Promise<DecideCandidateResponse> {
  return apiPost<DecideCandidateResponse>(
    `/contacts/candidates/${encodeURIComponent(id)}/confirm`,
    {},
    opts,
  );
}

export async function rejectCandidate(
  id: string,
  opts: ApiPostOptions = {},
): Promise<DecideCandidateResponse> {
  return apiPost<DecideCandidateResponse>(
    `/contacts/candidates/${encodeURIComponent(id)}/reject`,
    {},
    opts,
  );
}

/** Result of an operator-triggered duplicate scan (counts only). */
export interface DuplicateScanResponse {
  scanned_contacts: number;
  generated: number;
  inserted: number;
  updated: number;
  kept_decided: number;
  deleted_stale_pending: number;
}

/** POST /contacts/candidates/scan — re-run the duplicate finder on demand. */
export async function scanForDuplicates(opts: ApiPostOptions = {}): Promise<DuplicateScanResponse> {
  return apiPost<DuplicateScanResponse>('/contacts/candidates/scan', {}, opts);
}

export type ManualLanguage = 'en' | 'ru' | 'ar';

export interface PatchLanguageResponse {
  id: string;
  preferred_language: string | null;
  language_source: 'manual' | null;
  language_confidence: null;
}

export async function patchContactLanguage(
  id: string,
  language: ManualLanguage | null,
  opts: ApiPostOptions = {},
): Promise<PatchLanguageResponse> {
  return apiPatch<PatchLanguageResponse>(
    `/contacts/${encodeURIComponent(id)}/language`,
    { language },
    opts,
  );
}

/** batch-64/73 — per-contact auto-respond (testing); per-channel go-live. */
export interface AutoRespondConfigDto {
  id: string;
  auto_respond_enabled: boolean;
  auto_respond_prompt: string | null;
  auto_respond_channels: string[];
}

export function putAutoRespond(
  contactId: string,
  body: { enabled: boolean; prompt?: string | null; channels?: string[] },
  opts: ApiPostOptions = {},
): Promise<AutoRespondConfigDto> {
  return apiPut<AutoRespondConfigDto>(
    `/contacts/${encodeURIComponent(contactId)}/auto-respond`,
    body,
    opts,
  );
}

/* ---------- batch-85: contact knowledge base (review queue) ---------- */

export interface KnowledgeFactDto {
  id: string;
  category: string | null;
  content: string;
  status: 'pending' | 'confirmed' | 'rejected';
  confidence: number | null;
  source_thread_id: string | null;
  /** Wave-2 — provenance for the fact ledger (badge in the Knowledge tab). */
  source_type: 'meeting' | 'conversation' | 'analysis' | 'manual';
  learned_at: string;
  reviewed_at: string | null;
}
export interface KnowledgeProbeDto {
  id: string;
  content: string;
  source_thread_id: string | null;
  learned_at: string;
}
export interface ContactKnowledgeResponse {
  contact_id: string;
  facts: KnowledgeFactDto[];
  probes: KnowledgeProbeDto[];
}

export function getContactKnowledge(
  contactId: string,
  opts: FetchOptions = {},
): Promise<ContactKnowledgeResponse> {
  return apiGet<ContactKnowledgeResponse>(
    `/contacts/${encodeURIComponent(contactId)}/knowledge`,
    opts,
  );
}

export function reviewKnowledgeFact(
  contactId: string,
  factId: string,
  status: 'confirmed' | 'rejected' | 'pending',
  opts: ApiPostOptions = {},
): Promise<{ id: string; status: string }> {
  return apiPatch<{ id: string; status: string }>(
    `/contacts/${encodeURIComponent(contactId)}/knowledge/${encodeURIComponent(factId)}`,
    { status },
    opts,
  );
}

export function addKnowledgeFact(
  contactId: string,
  body: { content: string; category?: string | null },
  opts: ApiPostOptions = {},
): Promise<{ id: string; content: string; category: string | null; status: string }> {
  return apiPost(`/contacts/${encodeURIComponent(contactId)}/knowledge`, body, opts);
}

/** Bulk confirm/reject pending learned facts — scoped by ids, a confidence floor, or a contact. */
export function bulkReviewKnowledge(
  body: {
    status: 'confirmed' | 'rejected';
    ids?: string[];
    min_confidence?: number;
    contact_id?: string;
  },
  opts: ApiPostOptions = {},
): Promise<{ updated: number }> {
  return apiPost('/knowledge/bulk-review', body, opts);
}

export function deleteKnowledgeFact(
  contactId: string,
  factId: string,
  opts: ApiPostOptions = {},
): Promise<{ id: string; deleted: boolean }> {
  return apiDelete(
    `/contacts/${encodeURIComponent(contactId)}/knowledge/${encodeURIComponent(factId)}`,
    opts,
  );
}

/* ---------- B28.5: response feedback + contact preferences ---------- */

export interface CreateFeedbackResponse {
  feedback_id: string;
}

export function createFeedback(
  threadId: string,
  body: {
    feedback_type: FeedbackType;
    draft_id?: string | null;
    // R6 — names the analysis whose proposal was rated (R5 provenance).
    analysis_id?: string | null;
    reason?: string | null;
  },
  opts: ApiPostOptions = {},
): Promise<CreateFeedbackResponse> {
  return apiPost<CreateFeedbackResponse>(
    `/threads/${encodeURIComponent(threadId)}/feedback`,
    body,
    opts,
  );
}

export interface PatchContactProfileFactsResponse {
  contact_id: string;
  company: string | null;
  role: string | null;
  location: string | null;
  relationship_summary: string | null;
  last_updated_at: string;
}

export function patchContactProfileFacts(
  contactId: string,
  body: {
    company?: string | null;
    role?: string | null;
    location?: string | null;
    relationship_summary?: string | null;
  },
  opts: ApiPostOptions = {},
): Promise<PatchContactProfileFactsResponse> {
  return apiPatch<PatchContactProfileFactsResponse>(
    `/contacts/${encodeURIComponent(contactId)}/profile-facts`,
    body,
    opts,
  );
}

/* ---------- send intents (Batch B adds kill-switched LinkedIn dispatch) ---------- */

export type SendIntentStatus =
  | 'draft'
  | 'review_required'
  | 'blocked'
  | 'ready'
  | 'cancelled'
  // Batch B dispatch states:
  | 'dispatching'
  | 'sent'
  | 'failed';

export interface SendIntentDto {
  id: string;
  masked_id: string;
  thread_id: string;
  masked_thread_id: string;
  draft_id: string;
  masked_draft_id: string;
  channel: string;
  status: SendIntentStatus;
  safety_state: {
    blocked: boolean;
    recommended_status: string | null;
    evaluated_at: string | null;
  };
  risk_flags: string[];
  warnings: string[];
  recipient_confirmed_at: string | null;
  channel_confirmed_at: string | null;
  final_confirmed_at: string | null;
  cancelled_at: string | null;
  /** Batch B — set when a LinkedIn dispatch succeeded. */
  dispatched_at: string | null;
  /** Batch B — masked carrier message id on a successful send. */
  provider_message_id_masked: string | null;
  /** Batch B — present only on a failed-dispatch PATCH response. */
  dispatch_error?: string;
  /** batch-409 (P1-26) - a 5xx / reset / timeout: the message MAY have been delivered. */
  delivery_unknown?: boolean;
  created_at: string;
  updated_at: string;
}

/** GET /send-config — whether the runtime kill switch is on. */
export interface SendConfigDto {
  send_enabled: boolean;
  channels: string[];
}

/** GET /channel-health (batch-77) — per-channel connector health. */
export interface ChannelHealthDto {
  channel: string;
  connected: boolean;
  connection_status: string | null;
  last_inbound_at: string | null;
  healthy: boolean;
  /** batch-80 — is a Unipile webhook to this receiver covering the account? null = unknown. */
  webhook_subscribed: boolean | null;
  /** batch-103 — newest real-time webhook received for this account. */
  last_webhook_at?: string | null;
  /** batch-103 — real-time delivery looks broken (inbound advancing via safety-net, webhook frozen). null = no recent inbound to judge. */
  realtime_stale?: boolean | null;
}
export interface ChannelHealthResponse {
  checked_at: string;
  /** batch-279 — false ⇒ the live Unipile accounts call failed this cycle (e.g. a
   *  401); the channel rows are from the last good fetch, not verified now. */
  unipile_reachable?: boolean;
  channels: ChannelHealthDto[];
}
export function getChannelHealth(opts: FetchOptions = {}): Promise<ChannelHealthResponse> {
  return apiGet<ChannelHealthResponse>('/channel-health', opts);
}

/** GET /automation-activity (B3) — read-only feed of what the AI did automatically. */
export interface AutomationActivityItem {
  kind: string;
  action: string;
  status: string;
  contact_name: string | null;
  at: string;
  error: boolean;
}
export interface AutomationActivityResponse {
  items: AutomationActivityItem[];
}
export function getAutomationActivity(
  opts: FetchOptions = {},
): Promise<AutomationActivityResponse> {
  return apiGet<AutomationActivityResponse>('/automation-activity', opts);
}

/** GET /system-health (batch-113) — cost + worker + analysis-failure visibility. */
export interface SystemHealthDailyCost {
  day: string;
  cost_usd: number;
  analyses: number;
}
export interface SystemHealthResponse {
  checked_at: string;
  cost: {
    today_usd: number;
    yesterday_usd: number;
    trailing_avg_usd: number;
    spike: boolean;
    spike_ratio: number | null;
    days: SystemHealthDailyCost[];
  };
  worker: {
    alive: boolean;
    last_sync_at: string | null;
    minutes_since_sync: number | null;
    last_analysis_at: string | null;
    minutes_since_analysis: number | null;
  };
  analyses: {
    failed_24h: number;
    success_24h: number;
    failure_alert: boolean;
    recent_error_codes: string[];
  };
  // batch-157 — LinkedIn AI comment-draft health.
  comment_drafts?: {
    failed_24h: number;
    total_24h: number;
    failure_alert: boolean;
  };
  // batch-164 — off-host backup status.
  backups?: {
    offhost_configured: boolean;
  };
  // batch-276 — ingest safety-net / Unipile outbound-auth health (backfill 401 rate).
  ingest?: {
    status: 'ok' | 'degraded' | 'failing';
    failed: number;
    total: number;
    auth_failed: number;
    alert: boolean;
    reason: 'unipile_auth' | 'backfill_errors' | null;
  };
}
export function getSystemHealth(opts: FetchOptions = {}): Promise<SystemHealthResponse> {
  return apiGet<SystemHealthResponse>('/system-health', opts);
}

/** GET /engage-config (batch-120) — which LinkedIn engagement actions are armed. */
export interface EngageConfigDto {
  engage_enabled: boolean;
  react_enabled: boolean;
  comment_enabled: boolean;
  max_per_day: number;
}
export function getEngageConfig(opts: FetchOptions = {}): Promise<EngageConfigDto> {
  return apiGet<EngageConfigDto>('/engage-config', opts);
}
/** POST /posts/:socialId/react (batch-120) — react to a LinkedIn post (gated). */
export function reactToPost(
  socialId: string,
  reactionType: string,
  opts: ApiPostOptions = {},
): Promise<{ ok: boolean; audit_id: string }> {
  return apiPost(
    `/posts/${encodeURIComponent(socialId)}/react`,
    { reaction_type: reactionType },
    opts,
  );
}
/** POST /posts/:socialId/comment (batch-120) — comment on a LinkedIn post (gated). */
export function commentOnPost(
  socialId: string,
  text: string,
  opts: ApiPostOptions = {},
): Promise<{ ok: boolean; audit_id: string }> {
  return apiPost(`/posts/${encodeURIComponent(socialId)}/comment`, { text }, opts);
}

/** batch-122 (B3) — AI-drafted comment suggestion (read-only; routed via the worker). */
export interface CommentDraftDto {
  status: string;
  text: string | null;
  reason: string | null;
  /** R19 — when suppressed: 'caution' (surface, regulatory) | 'noise' (hide). */
  suppression_class?: 'caution' | 'noise' | null;
  /** batch-311 — AI evidence strip: model confidence (0..1) + facts the draft used. */
  confidence?: number | null;
  facts_used?: number | null;
  created_at: string | null;
}
export function draftComment(
  socialId: string,
  // batch-181 — `careful` = operator override: draft a measured comment even on a
  // sensitive/regulatory post the normal rules would leave empty.
  careful = false,
  opts: ApiPostOptions = {},
): Promise<{ ok: boolean; status: string }> {
  return apiPost(
    `/posts/${encodeURIComponent(socialId)}/draft-comment`,
    careful ? { careful: true } : {},
    opts,
  );
}
export function getCommentDraft(
  socialId: string,
  opts: FetchOptions = {},
): Promise<{ draft: CommentDraftDto | null }> {
  return apiGet(`/posts/${encodeURIComponent(socialId)}/comment-draft`, opts);
}

/**
 * batch-124 — LinkedIn engagement feed: every recent post (with a social_id)
 * across contacts + its latest AI comment draft. Powers the "LI Engagement" hub.
 */
export interface EngagementFeedItemDto {
  id: string;
  social_id: string | null;
  text: string | null;
  share_url: string | null;
  posted_at: string | null;
  reaction_count: number | null;
  comment_count: number | null;
  is_repost: boolean;
  contact_id: string | null;
  author_name: string | null;
  /** batch-126 — author network signal (e.g. 'FIRST_DEGREE'); null if profile not fetched. */
  network_distance?: string | null;
  in_network?: boolean;
  draft: CommentDraftDto | null;
}
export function getEngagementFeed(
  opts: FetchOptions = {},
): Promise<{ items: EngagementFeedItemDto[] }> {
  return apiGet('/linkedin/engagement-feed', opts);
}

/** R19 — enqueue an AI comment draft for every undrafted feed post (gated). */
export function draftFeed(opts: ApiPostOptions = {}): Promise<{ ok: boolean; queued: number }> {
  return apiPost('/linkedin/draft-feed', {}, opts);
}

/**
 * batch-148 — mark a feed post "not relevant": hides it from the feed AND teaches
 * the relevance judgement (negative example) so similar posts are filtered next time.
 */
export function markPostNotRelevant(
  socialId: string,
  opts: ApiPostOptions = {},
): Promise<{ ok: boolean }> {
  return apiPost(`/linkedin/posts/${encodeURIComponent(socialId)}/feedback`, {}, opts);
}

/** batch-146 — engagement history: reactions/comments the operator has posted. */
export interface EngagementHistoryItemDto {
  id: string;
  action: string; // 'post_reaction' | 'post_comment'
  payload: string | null; // reaction type, or the comment text
  status: string;
  requested_at: string | null;
  social_id: string | null;
  contact_id: string | null;
  author_name: string | null;
}
export function getEngagementHistory(
  opts: FetchOptions = {},
): Promise<{ items: EngagementHistoryItemDto[] }> {
  return apiGet('/linkedin/engagement-history', opts);
}

/**
 * batch-167/168 — "Job changes" hub: detected LinkedIn job changes + their AI
 * congrats DM drafts. Phase 2 is SHADOW — drafting + dismiss only, NO send.
 */
export interface JobChangeConfigDto {
  detect_enabled: boolean;
  drafts_enabled: boolean;
  /** batch-170 (Phase 3a) — congrats DM sending armed (operator-confirmed). */
  send_enabled?: boolean;
  /** Phase 3c — automatic congrats sending armed (no operator review). */
  autosend_enabled?: boolean;
  /** Phase 3c — automatic drafting armed (the loop drafts detected events → auto-send). */
  autodraft_enabled?: boolean;
}
export function getJobChangeConfig(opts: FetchOptions = {}): Promise<JobChangeConfigDto> {
  return apiGet<JobChangeConfigDto>('/job-change-config', opts);
}
/** batch-170 (Phase 3a) — send the operator-reviewed congrats DM (cookie session).
 * `text` is the operator's final/edited message; omitted → the stored ready draft. */
export function sendCongratsDm(
  id: string,
  text?: string,
  opts: ApiPostOptions = {},
): Promise<{ sent: boolean; audit_id: string }> {
  return apiPost(`/job-changes/${encodeURIComponent(id)}/dm`, text ? { text } : {}, opts);
}
export interface JobChangeDraftDto {
  status: string; // 'ready' | 'suppressed' | 'failed'
  text: string | null;
  reason: string | null;
  /** batch-311 — AI evidence strip: model confidence (0..1) + facts the draft used. */
  confidence?: number | null;
  facts_used?: number | null;
  created_at: string | null;
}
export interface JobChangeItemDto {
  id: string;
  kind: string; // 'new_company' | 'promotion'
  old_company: string | null;
  new_company: string | null;
  old_role: string | null;
  new_role: string | null;
  contact_id: string | null;
  contact_name: string | null;
  status: string; // 'detected' | 'drafted' | 'suppressed' | 'sent' | 'dismissed'
  detected_at: string | null;
  draft: JobChangeDraftDto | null;
}
export function getJobChanges(opts: FetchOptions = {}): Promise<{
  detect_enabled: boolean;
  drafts_enabled: boolean;
  send_enabled?: boolean;
  items: JobChangeItemDto[];
}> {
  return apiGet('/job-changes', opts);
}

// batch-297 — Job Changes History tab: the durable record of congrats DMs (sent + failed,
// operator + auto) from congrats_audit. Display-only (no ids).
export interface JobChangeHistoryItemDto {
  trigger: string; // 'operator' | 'auto'
  status: string; // 'sent' | 'failed'
  text: string | null;
  error: string | null;
  contact_name: string | null;
  kind: string | null; // 'new_company' | 'promotion'
  old_company: string | null;
  new_company: string | null;
  old_role: string | null;
  new_role: string | null;
  at: string | null;
}
export function getJobChangeHistory(
  opts: FetchOptions = {},
): Promise<{ items: JobChangeHistoryItemDto[] }> {
  return apiGet<{ items: JobChangeHistoryItemDto[] }>('/job-changes/history', opts);
}

/**
 * batch-206 (LinkedIn invitations — Phase 2b) — the received-invitation review hub.
 * Listing is read-only; assess enqueues an AI assessment (gated server-side).
 */
export interface InvitationItemDto {
  id: string;
  sender_name: string | null;
  sender_headline: string | null;
  sender_provider_id: string | null;
  /** batch-212 — URL-safe LinkedIn public identifier for the profile link. */
  sender_public_identifier: string | null;
  /** batch-213 — inviter location (enriched from their profile; may be null). */
  sender_location: string | null;
  /** batch-215 — inviter current company (enriched from their profile; may be null). */
  sender_company: string | null;
  message: string | null;
  shared_connections_count: number | null;
  status: string; // pending | accepted | declined | ignored
  verdict: 'accept' | 'decline' | 'review' | null;
  score: number | null;
  reason: string | null;
  assessed_at: string | null;
  /**
   * batch-398 — is the stored verdict the one the CURRENT rubric stands behind? null = not
   * assessed; 'legacy' = assessed before decisions recorded their policy; 'changed' = the rubric
   * was edited since. Legacy and changed decisions are never auto-acted until re-assessed.
   */
  policy_status?: 'legacy' | 'changed' | 'current' | null;
  /**
   * batch-403 - the model's own verdict. `verdict` above is the EFFECTIVE one: an accept with
   * fewer mutual connections than INVITATIONS_MIN_MUTUAL_CONNECTIONS reads 'review'.
   */
  ai_verdict?: 'accept' | 'decline' | 'review' | null;
  /** Set when the mutual-connection minimum held an accept for review; null otherwise. */
  mutual_gate?: {
    reason: 'below_minimum' | 'mutuals_unknown' | null;
    mutuals: number | null;
    minimum: number;
    note: string | null;
  } | null;
  welcome_status: string | null;
  welcome_text: string | null;
  received_at: string | null;
  created_at: string;
}
export function getInvitationsConfig(
  opts: FetchOptions = {},
): Promise<{ enabled: boolean; send_enabled?: boolean }> {
  return apiGet('/invitations-config', opts);
}
export function getInvitations(opts: FetchOptions = {}): Promise<{
  enabled: boolean;
  send_enabled?: boolean;
  items: InvitationItemDto[];
}> {
  return apiGet('/linkedin/invitations', opts);
}
export function assessAllInvitations(
  opts: ApiPostOptions = {},
): Promise<{ enqueued: number; considered: number; remaining?: number }> {
  return apiPost('/linkedin/invitations/assess-all', {}, opts);
}
/**
 * batch-208 (Phase 3a) — operator-confirmed accept/decline (cookie session). On
 * accept the welcome DM (operator's edited text or the stored draft) is sent via
 * the gated dispatch. Gated server-side on INVITATIONS_SEND_ENABLED.
 */
export function acceptInvitation(
  id: string,
  welcomeText?: string,
  opts: ApiPostOptions = {},
): Promise<{ ok: boolean; welcome_sent: boolean; welcome_error?: string | null }> {
  return apiPost(
    `/linkedin/invitations/${encodeURIComponent(id)}/accept`,
    welcomeText ? { welcomeText } : {},
    opts,
  );
}
export function declineInvitation(id: string, opts: ApiPostOptions = {}): Promise<{ ok: boolean }> {
  return apiPost(`/linkedin/invitations/${encodeURIComponent(id)}/decline`, {}, opts);
}
/**
 * batch-212 — refine the AI welcome via an operator guidance note (re-assess
 * steered, mirroring the messages composer's Refine). The updated welcome arrives
 * on the next `getInvitations` refetch.
 */
export function refineInvitationWelcome(
  id: string,
  guidance: string,
  opts: ApiPostOptions = {},
): Promise<{ enqueued: boolean }> {
  return apiPost(
    `/linkedin/invitations/${encodeURIComponent(id)}/refine-welcome`,
    { guidance },
    opts,
  );
}

/** R20 — accept/decline history for the Connection-requests queue. */
export interface InvitationHistoryItemDto {
  id: string;
  decision: string; // 'accept' | 'decline'
  status: string; // 'done' | 'failed' | 'attempted'
  requested_at: string | null;
  sender_name: string | null;
}
export function getInvitationHistory(
  opts: FetchOptions = {},
): Promise<{ items: InvitationHistoryItemDto[] }> {
  return apiGet('/linkedin/invitation-history', opts);
}

/**
 * batch-411 (ROADMAP P1-28, slice 1) - the HELD WELCOMES. An unattended accept sends no welcome while
 * auto-welcome is off; the worker records one held welcome per accept and the operator sends or
 * suppresses it from the hub. `delivery_unknown` marks a send the provider did not confirm: the message
 * may have gone out, so the hub says to check the conversation before sending again.
 */
export interface InvitationWelcomeDto {
  id: string;
  /** held | scheduled | failed | attempted | sent | suppressed. */
  status: string;
  /** batch-420 - null only while a scheduled welcome's AI line is still being written. */
  welcome_text: string | null;
  /**
   * batch-420 - why it was not sent at accept time: auto_welcome_off | auto_scheduled | screened |
   * line_failed | hub_welcome_failed. Absent from an older API.
   */
  held_reason?: string;
  /** batch-420 - who sent it: operator | auto. Absent from an older API. */
  trigger?: string;
  error: string | null;
  delivery_unknown: boolean;
  created_at: string;
  finalized_at: string | null;
  sender_name: string | null;
  sender_headline: string | null;
  sender_company: string | null;
  /**
   * batch-413 - the regulated / ADGM-contact screen, worked out by the API when the list is read.
   * Advisory: it never blocks a send. Null for a sent or skipped welcome; absent from an older API.
   */
  screen?: {
    screened: boolean;
    reasons: Array<{ code: string; label: string }>;
  } | null;
  /** batch-413 - the neutral wording (CRM mailbox only), offered only for a screened welcome. */
  referral_draft?: string | null;
}
export function getInvitationWelcomes(
  view: 'open' | 'history' = 'open',
  opts: FetchOptions = {},
): Promise<{ view: string; welcomes: InvitationWelcomeDto[] }> {
  return apiGet(`/linkedin/invitation-welcomes?view=${view}`, opts);
}
export function getInvitationWelcomeCount(opts: FetchOptions = {}): Promise<{ open: number }> {
  return apiGet('/linkedin/invitation-welcomes/count', opts);
}
export function deliverInvitationWelcome(
  id: string,
  text?: string,
  opts: ApiPostOptions = {},
): Promise<{ ok: boolean }> {
  return apiPost(
    `/linkedin/invitation-welcomes/${encodeURIComponent(id)}/deliver`,
    text ? { text } : {},
    opts,
  );
}
export function suppressInvitationWelcome(
  id: string,
  opts: ApiPostOptions = {},
): Promise<{ ok: boolean }> {
  return apiPost(`/linkedin/invitation-welcomes/${encodeURIComponent(id)}/suppress`, {}, opts);
}

export function draftJobChange(
  id: string,
  opts: ApiPostOptions = {},
): Promise<{ enqueued: boolean }> {
  return apiPost(`/job-changes/${encodeURIComponent(id)}/draft`, {}, opts);
}
export function dismissJobChange(
  id: string,
  opts: ApiPostOptions = {},
): Promise<{ dismissed: boolean }> {
  return apiPost(`/job-changes/${encodeURIComponent(id)}/dismiss`, {}, opts);
}

// ───────── batch-K3b — keep-in-touch (proactive check-ins) hub ─────────
export interface KeepInTouchConfigDto {
  enabled: boolean;
  /** Drafting + operator-confirmed sending armed (KEEP_IN_TOUCH_ENABLED + SEND_PATH_ENABLED). */
  send_enabled?: boolean;
}
export function getKeepInTouchConfig(opts: FetchOptions = {}): Promise<KeepInTouchConfigDto> {
  return apiGet<KeepInTouchConfigDto>('/keep-in-touch-config', opts);
}
export interface KeepInTouchSuggestionDto {
  id: string;
  contact_id: string;
  contact_name: string | null;
  trigger: string; // 'cadence' | 'dormant' | 'news' | 'birthday'
  status: string; // 'pending' | 'drafted' | 'ready' | 'suppressed' | 'snoozed' | …
  source_ref: string | null;
  draft_text: string | null;
  reason: string | null;
  /** batch-311/312 — AI evidence strip: model confidence (0..1) + facts the draft used. */
  confidence?: number | null;
  facts_used?: number | null;
  suggested_at: string | null;
  due_at: string | null;
  /** R18 — time-based snooze; a future value hides the suggestion from the hub. */
  snooze_until: string | null;
}
export function getKeepInTouchSuggestions(
  opts: FetchOptions = {},
): Promise<{ items: KeepInTouchSuggestionDto[] }> {
  return apiGet('/keep-in-touch/suggestions', opts);
}
export function draftKeepInTouchSuggestion(
  id: string,
  opts: ApiPostOptions = {},
): Promise<{ ok: boolean }> {
  return apiPost(`/keep-in-touch/${encodeURIComponent(id)}/draft`, {}, opts);
}
export function snoozeKeepInTouchSuggestion(
  id: string,
  until: string | null,
  opts: ApiPostOptions = {},
): Promise<{ ok: boolean }> {
  return apiPost(`/keep-in-touch/${encodeURIComponent(id)}/snooze`, { until }, opts);
}
export function skipKeepInTouchSuggestion(
  id: string,
  opts: ApiPostOptions = {},
): Promise<{ ok: boolean }> {
  return apiPost(`/keep-in-touch/${encodeURIComponent(id)}/skip`, {}, opts);
}
/** Operator-confirmed send (cookie session). `text` = the final/edited message. */
export function dispatchKeepInTouchSuggestion(
  id: string,
  text?: string,
  opts: ApiPostOptions = {},
): Promise<{ ok: boolean; channel: string }> {
  return apiPost(`/keep-in-touch/${encodeURIComponent(id)}/dispatch`, text ? { text } : {}, opts);
}
/** Enrol / update a contact's keep-in-touch settings (tier / cadence / pause / dates). */
export function setContactKeepInTouch(
  id: string,
  body: {
    tier?: string | null;
    interval_days?: number | null;
    paused?: boolean;
    important_dates?: Array<{
      kind: 'birthday' | 'custom';
      month: number;
      day: number;
      year?: number;
      label?: string;
    }>;
  },
  opts: ApiPostOptions = {},
): Promise<{ ok: boolean }> {
  return apiPost(`/contacts/${encodeURIComponent(id)}/keep-in-touch`, body, opts);
}

/**
 * POST /threads/:id/send-intents — create or return the active intent
 * for a draft. INERT: no dispatch happens regardless of status.
 */
export function createSendIntent(
  threadId: string,
  body: { draft_id: string },
  opts: ApiPostOptions = {},
): Promise<SendIntentDto> {
  return apiPost<SendIntentDto>(
    `/threads/${encodeURIComponent(threadId)}/send-intents`,
    body,
    opts,
  );
}

/**
 * PATCH /send-intents/:id — stamp a confirmation step or cancel.
 * INERT: no dispatch happens even when status becomes 'ready'.
 */
export function patchSendIntent(
  intentId: string,
  body: {
    recipient_confirmed?: boolean;
    channel_confirmed?: boolean;
    final_confirmed?: boolean;
    cancel?: boolean;
  },
  opts: ApiPostOptions = {},
): Promise<SendIntentDto> {
  return apiPatch<SendIntentDto>(`/send-intents/${encodeURIComponent(intentId)}`, body, opts);
}

/**
 * GET /send-config — does the server have the LinkedIn send path switched on?
 * The dashboard uses this to decide whether to render the dispatch affordance;
 * it can't read server env directly.
 */
export async function getSendConfig(params: GetThreadParams = {}): Promise<SendConfigDto> {
  return apiGet<SendConfigDto>('/send-config', params);
}

/**
 * R4 — GET /work-summary. One PII-free count per outreach/admin destination, fed
 * by the SAME predicate as each destination's list (server lib/work-summary.ts).
 * The single source for hub/nav badges (count-truth — never derive from a feed's
 * .length). Inbox/follow-ups counts come from /threads/summary + /threads/followups.
 */
export interface DoneTodayDto {
  job_changes: number;
  keep_in_touch: number;
  engagement: number;
  invitations: number;
}

export interface WorkSummaryDto {
  engagement: number;
  job_changes: number;
  invitations: number;
  keep_in_touch: number;
  identity_matches: number;
  /** QA M4 — open connection (people-you-met) proposals. */
  connect: number;
  /** R16 — server-backed Done-today counts for the queue scoreboards. */
  done_today?: DoneTodayDto;
}

export async function getWorkSummary(params: GetThreadParams = {}): Promise<WorkSummaryDto> {
  return apiGet<WorkSummaryDto>('/work-summary', params);
}

/** batch-62 — the operator's persistent global AI style guide. */
export interface StyleGuideDto {
  style_guide: string;
}

export function getStyleGuide(params: GetThreadParams = {}): Promise<StyleGuideDto> {
  return apiGet<StyleGuideDto>('/operator/style-guide', params);
}

export function putStyleGuide(
  styleGuide: string,
  opts: ApiPostOptions = {},
): Promise<StyleGuideDto> {
  return apiPut<StyleGuideDto>('/operator/style-guide', { style_guide: styleGuide }, opts);
}

/** batch-133 — operator's LinkedIn engagement interests (drives the relevance filter). */
export interface EngagementInterestsDto {
  engagement_interests: string;
}
export function getEngagementInterests(
  params: GetThreadParams = {},
): Promise<EngagementInterestsDto> {
  return apiGet<EngagementInterestsDto>('/operator/engagement-interests', params);
}
export function putEngagementInterests(
  interests: string,
  opts: ApiPostOptions = {},
): Promise<EngagementInterestsDto> {
  return apiPut<EngagementInterestsDto>(
    '/operator/engagement-interests',
    { engagement_interests: interests },
    opts,
  );
}

/**
 * PATCH /send-intents/:id { dispatch: true } — Batch B. Dispatch a `ready`
 * LinkedIn intent. Forces cookie credentials: the server rejects bearer-only
 * for dispatch (session-required). On success the intent becomes `sent`; on a
 * carrier failure it becomes `failed` and the response carries `dispatch_error`.
 */
export function dispatchSendIntent(
  intentId: string,
  opts: ApiPostOptions = {},
): Promise<SendIntentDto> {
  return apiPatch<SendIntentDto>(
    `/send-intents/${encodeURIComponent(intentId)}`,
    { dispatch: true },
    { ...opts, forceCredentialsInclude: true },
  );
}

/* ---------- auth (cookie mode) ---------- */

export interface LoginResponse {
  ok: true;
  operator_id: string;
}

/**
 * POST /auth/login — exchange a password for a session cookie. The
 * cookie is set by the server with HttpOnly + SameSite=Lax. We do NOT
 * persist the password anywhere; once login resolves, the password is
 * dropped by the caller. Always uses credentials: 'include' so the
 * Set-Cookie response can stick.
 */
export async function login(password: string, opts: FetchOptions = {}): Promise<LoginResponse> {
  // Force cookie credentials regardless of mode — the whole point of
  // calling /auth/login is to receive a Set-Cookie response.
  return request<LoginResponse>(
    'POST',
    '/auth/login',
    { password },
    {
      ...opts,
      authMode: opts.authMode ?? (AUTH_MODE === 'bearer' ? 'cookie' : AUTH_MODE),
    },
  );
}

/**
 * POST /auth/logout — server clears the cookie (Max-Age=0). Idempotent;
 * 200 even when no cookie was present.
 */
export async function logout(opts: FetchOptions = {}): Promise<{ ok: true }> {
  return request<{ ok: true }>('POST', '/auth/logout', undefined, {
    ...opts,
    authMode: opts.authMode ?? (AUTH_MODE === 'bearer' ? 'cookie' : AUTH_MODE),
  });
}

export interface SessionInfo {
  operator_id: string;
  expires_at: number;
}

/**
 * GET /auth/me — 200 + session info on valid cookie, 401 otherwise.
 * Cookie-only by design (the API does not honor bearer here). Use
 * isCookieAuthEnabled() to decide whether to call this at all.
 */
export async function getSession(opts: FetchOptions = {}): Promise<SessionInfo> {
  return request<SessionInfo>('GET', '/auth/me', undefined, {
    ...opts,
    authMode: opts.authMode ?? (AUTH_MODE === 'bearer' ? 'cookie' : AUTH_MODE),
  });
}

/* ---------- passkey / WebAuthn (Batch A, auth hardening Stage 2) ---------- */

/**
 * True when the browser exposes the WebAuthn API. Cheap, synchronous; gate the
 * passkey affordances on it so unsupported browsers fall back to the password.
 */
export function passkeySupported(): boolean {
  return browserSupportsWebAuthn();
}

/** R23 — an enrolled passkey (masked) for the Setup → Security list. */
export interface PasskeyDto {
  id: string; // masked first4…last4
  label: string | null;
  device_type: string | null;
  backed_up: boolean | null;
  created_at: string;
  last_used_at: string | null;
}
export function listPasskeys(opts: FetchOptions = {}): Promise<{ credentials: PasskeyDto[] }> {
  return apiGet('/auth/webauthn/credentials', opts);
}

/**
 * Enrol a new passkey for the already-authenticated operator.
 *
 * Two round-trips: fetch creation options (server sets a signed challenge
 * cookie), run the browser ceremony, POST the attestation back to verify. The
 * caller must already be authed (bearer or session) — the register endpoints
 * are behind requireReadAuth. We keep the configured auth mode (so the bearer
 * header rides along in bearer/hybrid deployments) and additionally force
 * cookie credentials so the challenge cookie round-trips.
 */
export async function registerPasskey(
  label?: string,
  opts: FetchOptions = {},
): Promise<{ ok: true; credential_id_masked: string }> {
  const ceremony: FetchOptions = { ...opts, forceCredentialsInclude: true };
  const options = await request<PublicKeyCredentialCreationOptionsJSON>(
    'POST',
    '/auth/webauthn/register/options',
    undefined,
    ceremony,
  );
  const attestation = await startRegistration({ optionsJSON: options });
  return request<{ ok: true; credential_id_masked: string }>(
    'POST',
    '/auth/webauthn/register/verify',
    { response: attestation, label },
    ceremony,
  );
}

/**
 * Authenticate with a passkey and receive a session cookie. Public flow,
 * equivalent to login(password): fetch request options (server sets challenge
 * cookie), run the browser ceremony, POST the assertion to verify. On success
 * the server replies with Set-Cookie for the session, exactly like
 * POST /auth/login.
 */
export async function loginWithPasskey(opts: FetchOptions = {}): Promise<LoginResponse> {
  const ceremony: FetchOptions = {
    ...opts,
    forceCredentialsInclude: true,
    authMode: opts.authMode ?? (AUTH_MODE === 'bearer' ? 'cookie' : AUTH_MODE),
  };
  const options = await request<PublicKeyCredentialRequestOptionsJSON>(
    'POST',
    '/auth/webauthn/login/options',
    undefined,
    ceremony,
  );
  const assertion = await startAuthentication({ optionsJSON: options });
  return request<LoginResponse>(
    'POST',
    '/auth/webauthn/login/verify',
    { response: assertion },
    ceremony,
  );
}

/** Exposed for tests that need to assert the configured base URL. */
export const __config = { BASE_URL, BEARER, AUTH_MODE };

// ───────── batch-288 — MS Outlook calendar import (Phase 1) ─────────

/** Multipart upload (FormData). Same auth rules as the JSON client; never logs creds. */
async function uploadFile<T>(path: string, file: File, opts: FetchOptions = {}): Promise<T> {
  const fetchImpl = opts.fetchImpl ?? globalThis.fetch;
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  const mode: AuthMode = opts.authMode ?? AUTH_MODE;
  const headers: Record<string, string> = {};
  if (isBearerAuthEnabled(mode)) headers.Authorization = `Bearer ${BEARER}`;
  const form = new FormData();
  form.append('file', file, file.name);
  let res: Response;
  try {
    res = await fetchImpl(url, {
      method: 'POST',
      headers,
      body: form,
      signal: opts.signal,
      credentials:
        opts.forceCredentialsInclude || isCookieAuthEnabled(mode) ? 'include' : 'same-origin',
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError('network', 0, url, err instanceof Error ? err.message : String(err));
  }
  if (!res.ok) {
    const text = await safeReadText(res);
    throw new ApiError(kindFromStatus(res.status), res.status, url, text);
  }
  const text = await safeReadText(res);
  return (text.length === 0 ? undefined : JSON.parse(text)) as T;
}

export interface CalendarConfigDto {
  enabled: boolean;
  timezone: string;
}
export interface CalendarStatsDto {
  total_events: number;
  linked_contacts: number;
  unmatched_attendees: number;
  last_import_at: string | null;
}
export interface CalendarImportSummaryDto {
  event_count: number;
  with_start: number;
  linked_events: number;
  linked_attendees: number;
  unmatched_people: number;
  new_emails: number;
  by_method: { email: number; name_exact: number; alias_confirmed: number; unmatched: number };
  warnings: string[];
}
export interface CalendarUnmatchedItem {
  raw_key: string;
  key_type: 'name' | 'email';
  display: string;
  count: number;
}
export interface CalendarImportResultDto {
  summary: CalendarImportSummaryDto;
  import_id: string | null;
  unmatched: CalendarUnmatchedItem[];
}

export function getCalendarConfig(opts: FetchOptions = {}): Promise<CalendarConfigDto> {
  return apiGet<CalendarConfigDto>('/calendar-config', opts);
}
export function getCalendarStats(opts: FetchOptions = {}): Promise<CalendarStatsDto> {
  return apiGet<CalendarStatsDto>('/calendar/stats', opts);
}
export function previewCalendarImport(
  file: File,
  opts: FetchOptions = {},
): Promise<CalendarImportResultDto> {
  return uploadFile<CalendarImportResultDto>('/calendar/import/preview', file, opts);
}
export function commitCalendarImport(
  file: File,
  opts: FetchOptions = {},
): Promise<CalendarImportResultDto> {
  return uploadFile<CalendarImportResultDto>('/calendar/import/commit', file, opts);
}
export function confirmCalendarAlias(
  body: {
    raw_key: string;
    key_type: 'name' | 'email';
    contact_id: string | null;
    status: 'confirmed' | 'ignored';
  },
  opts: FetchOptions = {},
): Promise<{ ok: boolean; linked_attendees: number }> {
  return apiPost<{ ok: boolean; linked_attendees: number }>('/calendar/aliases', body, opts);
}

// Phase 2 — today's meetings agenda for the Today home.
export interface TodayAgendaContactDto {
  contact_id: string;
  name: string | null;
  last_interaction_at: string | null;
  needs_reply: boolean;
  thread_id: string | null;
}
export interface TodayAgendaMeetingDto {
  id: string;
  subject: string | null;
  start_at: string | null;
  end_at: string | null;
  all_day: boolean;
  is_online: boolean;
  location: string | null;
  categories: string[];
  contacts: TodayAgendaContactDto[];
}
export interface TodayAgendaDto {
  timezone: string;
  meetings: TodayAgendaMeetingDto[];
  needs_reply_count: number;
}
export function getCalendarToday(opts: FetchOptions = {}): Promise<TodayAgendaDto> {
  return apiGet<TodayAgendaDto>('/calendar/today', opts);
}

// Phase 3a — post-meeting follow-ups.
export interface CalendarFollowupConfigDto {
  enabled: boolean;
  send_enabled: boolean;
}
export interface CalendarFollowupItemDto {
  event_id: string;
  subject: string | null;
  start_at: string | null;
  contact_id: string;
  contact_name: string | null;
  draft_id: string | null;
  draft_status: string | null;
  draft_text: string | null;
  draft_reason: string | null;
  /** batch-311 — AI evidence strip: model confidence (0..1) + facts the draft used. */
  draft_confidence: number | null;
  draft_facts_used: number | null;
  sent: boolean;
}
export function getCalendarFollowupConfig(
  opts: FetchOptions = {},
): Promise<CalendarFollowupConfigDto> {
  return apiGet<CalendarFollowupConfigDto>('/calendar-followup-config', opts);
}
export function getCalendarFollowups(
  opts: FetchOptions = {},
): Promise<{ items: CalendarFollowupItemDto[] }> {
  return apiGet<{ items: CalendarFollowupItemDto[] }>('/calendar/followups', opts);
}
export function draftCalendarFollowup(
  eventId: string,
  contactId: string,
  opts: FetchOptions = {},
): Promise<{ enqueued: boolean }> {
  return apiPost<{ enqueued: boolean }>(
    `/calendar/events/${encodeURIComponent(eventId)}/followup`,
    { contact_id: contactId },
    opts,
  );
}
export function dispatchCalendarFollowup(
  draftId: string,
  text: string | undefined,
  opts: FetchOptions = {},
): Promise<{ ok: boolean; channel: string }> {
  return apiPost<{ ok: boolean; channel: string }>(
    `/calendar/followups/${encodeURIComponent(draftId)}/dispatch`,
    { text },
    opts,
  );
}

// batch-294 — Phase 3b-1: pre-meeting briefs.
export interface MeetingBriefAiDto {
  status: string; // ready | suppressed | failed
  text: string | null;
  reason: string | null;
  created_at: string;
}
export interface MeetingBriefContactDto {
  contact_id: string;
  name: string | null;
  known_facts: string[];
  last_interaction_at: string | null;
  thread_count: number;
  recent_channel: string | null;
  recent_activity: string | null;
  recent_activity_at: string | null;
  open_follow_up_thread_id: string | null;
  prior_meeting: { subject: string | null; start_at: string | null } | null;
  ai_brief: MeetingBriefAiDto | null;
}
export interface MeetingBriefDto {
  event_id: string;
  subject: string | null;
  start_at: string | null;
  contacts: MeetingBriefContactDto[];
}
export function getMeetingBrief(
  eventId: string,
  opts: FetchOptions = {},
): Promise<MeetingBriefDto> {
  return apiGet<MeetingBriefDto>(`/calendar/events/${encodeURIComponent(eventId)}/brief`, opts);
}
export function synthesizeMeetingBrief(
  eventId: string,
  contactId: string,
  opts: FetchOptions = {},
): Promise<{ enqueued: boolean }> {
  return apiPost<{ enqueued: boolean }>(
    `/calendar/events/${encodeURIComponent(eventId)}/brief/synthesize`,
    { contact_id: contactId },
    opts,
  );
}

// batch-296 — Phase 3b-2: post-meeting knowledge capture (facts → review queue).
export function captureMeetingKnowledge(
  eventId: string,
  contactId: string,
  opts: FetchOptions = {},
): Promise<{ enqueued: boolean }> {
  return apiPost<{ enqueued: boolean }>(
    `/calendar/events/${encodeURIComponent(eventId)}/capture-knowledge`,
    { contact_id: contactId },
    opts,
  );
}

// batch-301 — connect-with-meeting-people (Phase 1b): the "Connect with people you met" hub.
export interface ConnectCandidateDto {
  providerId: string | null;
  name: string | null;
  headline: string | null;
  location: string | null;
  profileUrl: string | null;
  networkDistance: string | null;
}
export interface ConnectProposalDto {
  id: string;
  attendee_name: string | null;
  attendee_email: string | null;
  attendee_company: string | null;
  meeting_subject: string | null;
  meeting_start_at: string | null;
  candidates: ConnectCandidateDto[];
  chosen_provider_id: string | null;
  status: string;
  created_at: string;
}
export function getConnectConfig(
  opts: FetchOptions = {},
): Promise<{ enabled: boolean; send_enabled?: boolean; invite_template?: string }> {
  return apiGet<{ enabled: boolean; send_enabled?: boolean; invite_template?: string }>(
    '/connect-config',
    opts,
  );
}
export function sendConnectInvite(
  id: string,
  message: string | undefined,
  opts: FetchOptions = {},
): Promise<{ sent: boolean; invitation_id: string | null }> {
  return apiPost<{ sent: boolean; invitation_id: string | null }>(
    `/connect/proposals/${encodeURIComponent(id)}/dm`,
    { message },
    opts,
  );
}
export function getConnectProposals(
  opts: FetchOptions = {},
): Promise<{ items: ConnectProposalDto[] }> {
  return apiGet<{ items: ConnectProposalDto[] }>('/connect/proposals', opts);
}
export function confirmConnectProposal(
  id: string,
  providerId: string,
  opts: FetchOptions = {},
): Promise<{ confirmed: boolean }> {
  return apiPost<{ confirmed: boolean }>(
    `/connect/proposals/${encodeURIComponent(id)}/confirm`,
    { provider_id: providerId },
    opts,
  );
}
export function dismissConnectProposal(
  id: string,
  opts: FetchOptions = {},
): Promise<{ dismissed: boolean }> {
  return apiPost<{ dismissed: boolean }>(
    `/connect/proposals/${encodeURIComponent(id)}/dismiss`,
    {},
    opts,
  );
}

/* ---------- batch-341: contact-hygiene review hub ---------- */

/**
 * batch-338/339 — the contact-hygiene engine's review surface. Findings flag
 * likely-junk contacts (archive candidates) or attribution problems (a linked
 * contact whose sender name doesn't match). DTOs mirror the API
 * (apps/api/src/routes/contact-hygiene.ts) EXACTLY — snake_case, ids only on the
 * finding + contact (never a raw identity id; `signals` carries dictionary words
 * and counts, never ids).
 */
export type HygieneDetector = 'junk' | 'attribution';
export type HygieneStatus = 'pending' | 'approved' | 'dismissed' | 'auto_applied';

export interface HygieneFindingDto {
  id: string;
  detector: HygieneDetector;
  class: string;
  confidence: number;
  /** Class-specific evidence: word arrays (generic_locals/channels) + counts. No ids. */
  signals: Record<string, unknown> | null;
  status: HygieneStatus;
  applied_action: string | null;
  created_at: string;
  contact: {
    id: string;
    name: string | null;
    channels: string[];
    archived: boolean;
  };
}

export interface HygieneFindingsResponse {
  items: HygieneFindingDto[];
  counts: { by_class: Record<string, number>; total: number };
}

export interface GetHygieneFindingsParams {
  detector?: HygieneDetector;
  status?: HygieneStatus;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
}

export function getHygieneFindings(
  params: GetHygieneFindingsParams = {},
): Promise<HygieneFindingsResponse> {
  const search = new URLSearchParams();
  if (params.detector) search.set('detector', params.detector);
  if (params.status) search.set('status', params.status);
  const qs = search.toString();
  return apiGet<HygieneFindingsResponse>(`/contact-hygiene/findings${qs ? `?${qs}` : ''}`, {
    signal: params.signal,
    fetchImpl: params.fetchImpl,
  });
}

/** Outcome shared by approve/dismiss: 'applied' or (junk-archive) 'skipped_protected'. */
export interface HygieneDecisionResponse {
  id: string;
  outcome: 'applied' | 'skipped_protected';
}

export function approveHygieneFinding(
  id: string,
  opts: ApiPostOptions = {},
): Promise<HygieneDecisionResponse> {
  return apiPost<HygieneDecisionResponse>(
    `/contact-hygiene/findings/${encodeURIComponent(id)}/approve`,
    {},
    opts,
  );
}

export function dismissHygieneFinding(
  id: string,
  opts: ApiPostOptions = {},
): Promise<HygieneDecisionResponse> {
  return apiPost<HygieneDecisionResponse>(
    `/contact-hygiene/findings/${encodeURIComponent(id)}/dismiss`,
    {},
    opts,
  );
}

export interface BulkHygieneResponse {
  applied: number;
  skipped_protected: number;
  matched: number;
}

export function bulkHygiene(
  body: { detector: HygieneDetector; class: string; action: 'approve' | 'dismiss' },
  opts: ApiPostOptions = {},
): Promise<BulkHygieneResponse> {
  return apiPost<BulkHygieneResponse>('/contact-hygiene/bulk', body, opts);
}

/** Result of an on-demand scan (the engine's own counts summary; opaque shape). */
export type HygieneScanResponse = Record<string, unknown>;

export function runHygieneScan(opts: ApiPostOptions = {}): Promise<HygieneScanResponse> {
  return apiPost<HygieneScanResponse>('/contact-hygiene/scan', {}, opts);
}

export interface HygieneConfigDto {
  enabled: boolean;
  auto_archive: boolean;
}

export function getHygieneConfig(opts: FetchOptions = {}): Promise<HygieneConfigDto> {
  return apiGet<HygieneConfigDto>('/contact-hygiene/config', opts);
}

/** Pending counts per detector — the count-truth source for the hub badge/tabs. */
export interface HygieneSummaryDto {
  junk: number;
  attribution: number;
}

export function getHygieneSummary(opts: FetchOptions = {}): Promise<HygieneSummaryDto> {
  return apiGet<HygieneSummaryDto>('/contact-hygiene/summary', opts);
}
